/**
 * PostHog Analytics
 *
 * Lightweight wrapper for PostHog event tracking.
 * Initialized in main.tsx, used across the app via these helpers.
 *
 * Key events tracked:
 * - Page views (automatic via SPA pageview capture)
 * - CTA clicks (request_demo, try_demo, view_pricing, signup_start)
 * - Form submissions (lead_captured, checkout_started)
 * - Email campaign attribution (UTM params captured automatically)
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = (import.meta.env.VITE_POSTHOG_KEY || '').trim();
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com').trim();

let initialized = false;

/**
 * Initialize PostHog. Call once in main.tsx.
 * No-ops gracefully if VITE_POSTHOG_KEY is not set.
 */
export function initPostHog(): void {
  if (!POSTHOG_KEY || initialized) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false, // We handle this manually for SPA route changes
    capture_pageleave: true,
    autocapture: true, // Auto-captures clicks, form submissions, etc.
    persistence: 'localStorage+cookie',
    person_profiles: 'always', // Track anonymous visitors (not just identified users)
    // Mask sensitive inputs by default
    mask_all_text: false,
    mask_all_element_attributes: false,
  });

  initialized = true;
}

/**
 * Track a page view. Call on route changes.
 */
export function trackPageView(path?: string): void {
  if (!initialized) return;
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    path: path || window.location.pathname,
  });
}

/**
 * Track a custom event.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

/**
 * Identify a known user (e.g., after login).
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.identify(userId, properties);
}

/**
 * Reset identity (e.g., on logout).
 */
export function resetUser(): void {
  if (!initialized) return;
  posthog.reset();
}

// Re-export posthog instance for advanced usage
export { posthog };
